import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useApi";

function normalizeRoleList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is string => typeof entry === "string")
      .map((role) => role.trim().toLowerCase())
      .filter(Boolean);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return [value.trim().toLowerCase()];
  }
  return [];
}

export function useAccessControl() {
  const { user, loading, isStub } = useAuth();
  const me = useCurrentUser({ enabled: !!user && !loading && !isStub });
  const [claimRoles, setClaimRoles] = useState<string[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user || isStub) {
      setClaimRoles([]);
      setClaimsLoading(false);
      return;
    }

    setClaimsLoading(true);
    new Promise<Awaited<ReturnType<typeof user.getIdTokenResult>>>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("token claims timeout")), 8_000);
      user.getIdTokenResult().then(
        (r) => { clearTimeout(timer); resolve(r); },
        (e) => { clearTimeout(timer); reject(e); }
      );
    })
      .then((tokenResult) => {
        if (cancelled) return;
        const roles = [
          ...normalizeRoleList(tokenResult.claims.roles),
          ...normalizeRoleList(tokenResult.claims.role),
        ];
        setClaimRoles(Array.from(new Set(roles)));
      })
      .catch(() => {
        if (!cancelled) setClaimRoles([]);
      })
      .finally(() => {
        if (!cancelled) setClaimsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, isStub]);

  const dbRoles = useMemo(() => {
    if (isStub) return ["admin"];
    return (me.data?.roles ?? []).map((role) => role.toLowerCase());
  }, [isStub, me.data?.roles]);

  const roles = useMemo(() => {
    if (isStub) return ["admin"];
    return Array.from(new Set([...dbRoles, ...claimRoles]));
  }, [isStub, dbRoles, claimRoles]);

  const permissions = useMemo<string[]>(() => {
    if (isStub) return ["financeiro", "diretoria", "comercial", "comunicacao"];
    const raw = me.data?.permissions;
    if (!Array.isArray(raw)) return [];
    return raw.filter((p): p is string => typeof p === "string");
  }, [isStub, me.data?.permissions]);

  // Hardening: privilégio de admin no frontend depende somente do role local.
  const isAdmin = dbRoles.includes("admin");
  const roleLabel = isAdmin ? "Admin" : "Usuário";
  const accessLoading = loading || (!!user && !isStub && (me.isLoading || claimsLoading));

  return {
    authUser: user,
    appUser: me.data ?? null,
    roles,
    permissions,
    isAdmin,
    roleLabel,
    loading: accessLoading,
  };
}
